/**
 * «Сайт и контент» с вопросами клиентов (#843) — слоями (#2072, спека
 * docs/plans/2026-10-11-voice-report-layers.md §2.1–2.2, PR 2 §9): одна полоса
 * фильтра с меню выгрузки вкладки, сводка из трёх карточек — самый частый вопрос,
 * вопрос первой минуты, тема с вопросами без ответа, — и переключатель разделов
 * Частые вопросы | Первая минута | Темы | Черновик FAQ.
 *
 * Флаг `enableCustomerQuestions` открыт; шлюз сервера закрыт — пояснение
 * (`VoiceSiteClosed`), сбой блоков — ошибка с повтором при сохранённом фильтре.
 * Темы разговоров (#845 PR 6) — за своим флагом и со своим запросом (срез
 * `questions`): раздел и карточка темы есть и при сбое блоков вопросов; набор
 * пуст — нет ни раздела, ни карточки (кнопка «Задать темы» — в шапке раздела).
 *
 * Черновик FAQ (`VoiceFaqDraft`, §8 п. 6) смонтирован, пока открыта вкладка, и
 * при смене раздела не теряется: суточный лимит на него уже потрачен (хвост 2
 * ревью спеки, #2051). Под темой сборка ждёт ревизии темы (#2238, #2901): она
 * известна только из ответа блока тем, и черновик, оплаченный под неизвестной
 * ревизией, пропал бы после повтора этого ответа — упавший ответ тем
 * объясняется ошибкой с повтором в самом разделе.
 *
 * Фильтр темы (#2229, §2.5): блоки вопросов и черновик FAQ сужены темой,
 * строка фильтра берёт числа из эха ответа вопросов. Ответ без эха выбранной
 * темы — чисел нет, черновик под темой не собирается. Выгрузка подписана темой;
 * блок тем (#2009) фильтром не сужен и поэтому выгружается только без него.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceCustomerQuestions } from '@aica/api-types/schemas';
import { TabsContent } from '@/components/ui/tabs';
import { formatDateShort } from '@/lib/utils';
import { StreamFilterBar } from '../stream-filter/StreamFilterBar';
import { VoiceExportMenu } from './VoiceExportMenu';
import { VoiceFaqDraft } from './VoiceFaqDraft';
import { VoiceLayers, type VoiceLayerCard } from './VoiceLayers';
import { VoiceLoadError } from './VoiceLoadError';
import { VoiceLoading } from './VoiceLoading';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { VoiceLocked } from './VoiceLocked';
import { VoiceQuestionPanel } from './VoiceQuestionPanel';
import { VoiceQuestionsCoverage } from './VoiceQuestionsCoverage';
import { VoiceQuestionsNotes } from './VoiceQuestionsNotes';
import { VoiceSiteClosed } from './VoiceSiteClosed';
import { VoiceThemeFilterBlocks } from './VoiceThemeFilterBlocks';
import { VoiceChangesSection } from './VoiceChangesSection';
import { VoiceThemeFilterStrip } from './VoiceThemeFilterStrip';
import { tabSeriesWindow } from './useVoiceSeries';
import { changesBlock } from './voice-series-view-model';
import {
  themeChangeSection,
  themeExportSection,
  themePrintNotes,
  themesState,
} from './voice-themes-view-model';
import { VoiceThemesBlock } from './VoiceThemesBlock';
import { useVoiceThemeFilterTab, voiceThemeFilterTheme } from './useVoiceThemeFilter';
import { VOICE_TOP_ROWS } from './VoiceTopList';
import { useVoiceCustomerQuestions } from './useVoiceOfCustomer';
import { useVoiceThemesLayer } from './useVoiceThemesLayer';
import {
  customerQuestionsState,
  firstMinuteSection,
  firstMinuteTextKeys,
  questionsPrintNotes,
  questionSection,
  siteQuestionSections,
} from './voice-questions-view-model';
import {
  VOICE_SITE_SECTIONS,
  type VoiceSearch,
  type VoiceSiteSection,
} from './voice-search';
import {
  firstMinuteLead,
  frequentLead,
  summaryCardState,
  themeUnansweredLead,
} from './voice-summary-view-model';

export interface VoiceSiteQuestionsProps {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
  upgradeHref?: string;
  /** Open the theme set's panel — from the themes block (#845 PR 6). */
  onOpenThemes?: () => void;
}

export function VoiceSiteQuestions({
  search,
  onSearchChange,
  onOpenCall,
  upgradeHref,
  onOpenThemes,
}: VoiceSiteQuestionsProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  // The dedicated questions request, as on Sales: the full section answer
  // carries no questions blocks (code-review #1802).
  const {
    query: voice,
    stream,
    request,
  } = useVoiceCustomerQuestions(search, onSearchChange, { site: true });
  // The themes section and card read one answer of the tab (cut `questions`).
  const themesLayer = useVoiceThemesLayer(search, onSearchChange, 'questions', (ready) =>
    themeUnansweredLead(ready, t, search.voiceTheme)
  );
  const filterTheme = voiceThemeFilterTheme(search, themesLayer);
  const filter = useVoiceThemeFilterTab(search, filterTheme, [voice]);
  const { query: themes, available: themesAvailable, shown: themesShown } = themesLayer;

  const cqState = voice.data ? customerQuestionsState(voice.data) : null;
  // The server's gate is closed for this organization: the tab as it was,
  // without a filter for numbers it does not have. A gate that is open but
  // whose blocks failed is not that: it keeps the filters and offers a retry.
  if (cqState?.kind === 'closed') {
    return (
      <VoiceSiteClosed
        reason="off"
        search={search}
        onSearchChange={onSearchChange}
        onOpenCall={onOpenCall}
        upgradeHref={upgradeHref}
        onOpenThemes={onOpenThemes}
      />
    );
  }
  // The section itself is refused (403 with a code): the tab says why, once.
  const refusal =
    (voice.isError ? tierRefusalCode(voice.error) : null) ??
    (themesAvailable && themes.isError ? tierRefusalCode(themes.error) : null);
  if (refusal) return <VoiceLocked code={refusal} upgradeHref={upgradeHref} />;
  // The theme is being lifted after a refusal: the page reloads without it.
  if (filter.refused) return <VoiceLoading />;

  const cq = cqState?.kind === 'ready' ? cqState.cq : undefined;
  const sections = VOICE_SITE_SECTIONS.filter((id) => id !== 'themes' || themesShown);
  const section = sections.find((id) => id === search.voiceSection) ?? 'frequent';
  const open = (id: VoiceSiteSection) =>
    onSearchChange({ voiceSection: id === 'frequent' ? undefined : id });

  const questionsCard = (lead: typeof frequentLead) =>
    summaryCardState(voice, () => (cq ? lead(cq, t) : undefined));
  const cards: VoiceLayerCard<VoiceSiteSection>[] = [
    {
      id: 'frequent',
      title: t('voicePage.layers.summary.frequent'),
      state: questionsCard(frequentLead),
    },
    {
      id: 'firstMinute',
      title: t('voicePage.layers.summary.firstMinute'),
      state: questionsCard(firstMinuteLead),
    },
    ...(themesLayer.cardShown
      ? [
          {
            id: 'themes' as const,
            title: t('voicePage.layers.summary.themeUnanswered'),
            state: themesLayer.card,
          },
        ]
      : []),
  ];

  /** A questions section: coverage first — the denominator before the numerators — then the block, then the notes. */
  const questionsBody = (render: (ready: VoiceCustomerQuestions) => ReactNode) => {
    if (voice.isPending) return <VoiceLoading />;
    // A failed refetch keeps the previous answer: not drawn as current.
    if (!cq || voice.isError) {
      return <VoiceLoadError onRetry={() => void voice.refetch()} />;
    }
    return (
      <div className="space-y-4">
        <VoiceQuestionsCoverage cq={cq} />
        {render(cq)}
        <VoiceQuestionsNotes cq={cq}>
          <p>{t(firstMinuteTextKeys(cq).denominatorNote)}</p>
        </VoiceQuestionsNotes>
      </div>
    );
  };
  const panel = { search, onSearchChange, onOpenCall, limit: VOICE_TOP_ROWS };
  // «What changed» (#2394 §9 п. 7): the marked rows of the tab's own answers.
  const changesBody = () => (
    <VoiceChangesSection
      blocks={[
        changesBlock(voice, cq, (ready) => [
          questionSection(ready),
          firstMinuteSection(ready),
        ]),
        ...(themesShown
          ? [
              changesBlock(themes, themesLayer.ready, (ready) => [
                themeChangeSection(ready),
              ]),
            ]
          : []),
      ]}
      expandedRow={search.row}
      onToggleRow={(rowId) =>
        onSearchChange({ row: search.row === rowId ? undefined : rowId })
      }
      onOpenCall={onOpenCall}
      // Frequent questions are the tab's first section: the address omits it.
      sourceSection={(block) =>
        block === 'firstMinute' || block === 'themes' ? block : undefined
      }
    />
  );
  // Not narrowed by the filter: still drawn when the narrowed blocks are not.
  const themesBlock = themesShown ? (
    <VoiceThemesBlock
      query={themes}
      search={search}
      onSearchChange={onSearchChange}
      onOpenCall={onOpenCall}
      onOpenThemes={onOpenThemes}
      limit={VOICE_TOP_ROWS}
      upgradeHref={upgradeHref}
    />
  ) : null;
  const fetching = [voice, ...(themesAvailable ? [themes] : [])].some(
    (query) => query.isFetching && !query.isLoading
  );
  // The themes block leaves with the questions (#2009, `useVoiceThemesLayer`).
  const exportedThemes = themesLayer.exported.themes;

  return (
    <div className="space-y-4">
      <StreamFilterBar
        period={stream.period}
        navigation={stream.navigation}
        projectIds={stream.projectIds}
        callCategory={stream.callCategory}
        isLoading={fetching}
        onSearchChange={onSearchChange}
        extraActions={
          voice.isError || cqState?.kind === 'failed' || themesLayer.exported.failed ? (
            // A failed refetch keeps an answer the screen no longer shows.
            <VoiceExportMenu unavailable="failed" />
          ) : cq && voice.data && themesLayer.exported.settled && filter.blocksShown ? (
            <VoiceExportMenu
              audience="site"
              sections={[
                ...siteQuestionSections(cq),
                ...(exportedThemes ? [themeExportSection(exportedThemes)] : []),
              ]}
              data={{
                kind: 'questions',
                periodStart: voice.data.periodStart,
                periodEnd: voice.data.periodEnd,
                // Every block carries its own cap (`VoiceSection.cap`).
                truncated: false,
                prevTruncated: false,
              }}
              printNotes={[
                ...questionsPrintNotes(cq, t, formatDateShort, {
                  comparable: filter.comparable,
                  capsInBlocks: true,
                  hint: t(firstMinuteTextKeys(cq).hint),
                  notes: [
                    t(firstMinuteTextKeys(cq).denominatorNote),
                    t('voicePage.questions.wordingNote'),
                  ],
                }),
                ...(exportedThemes ? themePrintNotes(exportedThemes, t) : []),
              ]}
              theme={filter.exportTheme}
              // While a new filter loads, the screen still shows the previous
              // scope's numbers; an export would file them under the new name.
              disabled={voice.isPlaceholderData}
            />
          ) : (
            <VoiceExportMenu unavailable={filter.exportUnavailable ?? 'pending'} />
          )
        }
      />

      <VoiceThemeFilterStrip
        filter={filter}
        truncated={cq?.truncated ?? false}
        onSearchChange={onSearchChange}
      />

      <VoiceThemeFilterBlocks
        filter={filter}
        seriesWindow={tabSeriesWindow(stream.dateRange, [
          voice,
          ...(themesAvailable ? [themes] : []),
        ])}
        unnarrowed={themesBlock}
      >
        <VoiceLayers cards={cards} sections={sections} section={section} onOpen={open}>
          <TabsContent value="frequent">
            {questionsBody((ready) => (
              <VoiceQuestionPanel
                {...panel}
                cq={ready}
                section={questionSection(ready)}
              />
            ))}
          </TabsContent>
          <TabsContent value="firstMinute">
            {questionsBody((ready) => (
              <VoiceQuestionPanel
                {...panel}
                cq={ready}
                section={firstMinuteSection(ready)}
                hint={t(firstMinuteTextKeys(ready).hint)}
              />
            ))}
          </TabsContent>
          {themesBlock && <TabsContent value="themes">{themesBlock}</TabsContent>}
          {/* Kept mounted: a draft already paid for survives a section change. */}
          <TabsContent value="faq" forceMount className="space-y-4">
            {/* The draft is built from the questions: their failure is said here
              too, with a retry. Only while open — the section stays mounted. */}
            {section === 'faq' && !voice.isPending && (!cq || voice.isError) && (
              <VoiceLoadError onRetry={() => void voice.refetch()} />
            )}
            {/* Under a theme the draft waits for the theme's revision: when the
              themes answer failed, say so here with its retry — the button is
              off meanwhile (#2901). */}
            {section === 'faq' &&
              search.voiceTheme !== undefined &&
              themesAvailable &&
              (themes.isError ||
                (themes.data !== undefined &&
                  themesState(themes.data).kind === 'failed')) && (
                <VoiceLoadError onRetry={() => void themes.refetch()} />
              )}
            <VoiceFaqDraft
              params={request.params}
              requestKey={request.key}
              // Under a theme the draft is filed by its revision: not before
              // the themes block names it — neither while that answer is on
              // its way nor after it failed: a draft paid for under an unknown
              // revision is lost when a retry names it (#2901).
              disabled={
                voice.isPlaceholderData ||
                !cq ||
                (search.voiceTheme !== undefined &&
                  themesAvailable &&
                  filterTheme.revision === undefined)
              }
              themeRevision={filterTheme.revision}
            />
          </TabsContent>
          <TabsContent value="changes">{changesBody()}</TabsContent>
        </VoiceLayers>
      </VoiceThemeFilterBlocks>
    </div>
  );
}
